import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus, Mail, Building2, Users, Search, ChevronLeft, ChevronRight, Send, X, PhoneIncoming
} from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { PageHeader } from '@/shared/components/ui/PageHeader';
import { StatCard, StatGrid } from '@/shared/components/ui/StatCard';
import { Select } from '@/shared/components/ui/Select';
import { Table, type Column } from '@/shared/components/ui/Table';
import { Avatar } from '@/shared/components/ui/Avatar';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { useContacts } from '../hooks/useContacts';
import type { Contact, ContactsSort } from '../types';
import { useHasPermission } from '@/shared/permissions/useHasPermission';
import { useUser } from '@/shared/hooks/useAuth';
import { useUserOptions } from '@/shared/hooks/useUserOptions';
import { ROUTES } from '@/routes/paths';
import { useRowSelection } from '@/features/bulk/hooks/useRowSelection';
import { SelectCheckbox } from '@/features/bulk/components/SelectCheckbox';
import { ContactsBulkEmailModal } from '../components/ContactsBulkEmailModal';
import { CallerLookupModal } from '../components/CallerLookupModal';

const PAGE_SIZE = 200;

const SORT_OPTIONS: { value: ContactsSort; label: string }[] = [
  { value: 'date_desc', label: 'Newest first' },
  { value: 'date_asc', label: 'Oldest first' },
  { value: 'name_asc', label: 'Name (A-Z)' },
  { value: 'name_desc', label: 'Name (Z-A)' },
];

export function ContactsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useSearchParams();
  const canCreate = useHasPermission('contacts.insert');
  const canBulkEmail = useHasPermission('contacts.bulk_email');
  const [bulkEmailOpen, setBulkEmailOpen] = useState(false);
  const [callerLookupOpen, setCallerLookupOpen] = useState(false);
  const currentUser = useUser();
  // Every employee sees every contact. WHO added each one is tracked for the
  // Super Admin alone, so the "Created by" column and filter are theirs only.
  const canSeeWholeDirectory = currentUser?.role === 'superadmin';
  const { data: userOptions = [] } = useUserOptions(canSeeWholeDirectory);
  // ─── Search (server-side, debounced) ─────────────────────────────────────────
  const searchInput = query.get('q') ?? '';
  const [search, setSearch] = useState(searchInput.trim());
  const setFilter = (key: string, value: string) => setQuery((previous) => {
    const next = new URLSearchParams(previous);
    if (value) next.set(key, value); else next.delete(key);
    next.delete('page');
    return next;
  }, { replace: true });
  const setSearchInput = (value: string) => setFilter('q', value);
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // ─── Lightweight client filter (company) ─────────────────────────────────────
  const company = query.get('company') ?? '';
  const sort = (SORT_OPTIONS.find((option) => option.value === query.get('sort'))?.value ?? 'date_desc') as ContactsSort;
  const createdBy = query.get('owner') ?? '';
  const pageValue = Number(query.get('page') ?? 0);
  const page = Number.isSafeInteger(pageValue) && pageValue >= 0 ? pageValue : 0;
  const setCompany = (value: string) => setFilter('company', value);
  const setSort = (value: ContactsSort) => setFilter('sort', value);
  const setCreatedBy = (value: string) => setFilter('owner', value);
  const setPage = (update: (page: number) => number) => setQuery((previous) => {
    const next = new URLSearchParams(previous);
    next.set('page', String(update(page)));
    return next;
  }, { replace: true });

  const filters = useMemo(
    () => ({
      ...(search ? { search } : {}),
      ...(createdBy ? { createdBy } : {}),
      sort,
      compact: true,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [page, search, sort, createdBy],
  );
  const { data = { contacts: [], total: 0, stats: { total: 0, emailReachable: 0, uniqueCompanies: 0 } }, isLoading, isFetching, isError, refetch } = useContacts(filters);
  const { contacts, total, stats } = data;

  const companyOptions = useMemo(() => {
    const set = new Set<string>();
    contacts.forEach((c) => c.company && set.add(c.company));
    return [
      { value: '', label: 'All companies' },
      ...Array.from(set).sort().map((c) => ({ value: c, label: c })),
    ];
  }, [contacts]);

  const visible = useMemo(
    () => (company ? contacts.filter((c) => c.company === company) : contacts),
    [contacts, company],
  );
  const selection = useRowSelection(visible.map((c) => c.id));

  const withEmailCount = stats?.emailReachable ?? 0;
  const companyCount = stats?.uniqueCompanies ?? Math.max(0, companyOptions.length - 1);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total > 0 ? page * PAGE_SIZE + 1 : 0;
  const rangeEnd = Math.min((page + 1) * PAGE_SIZE, total);

  const handleCreate = () => {
    navigate(ROUTES.contactNew);
  };

  const handleRowClick = (contact: Contact) => {
    navigate(`/contacts/${contact.id}`, { state: { returnTo: location.pathname + location.search } });
  };

  const columns: Column<Contact>[] = [
    ...(canBulkEmail ? [{
      key: '_select',
      header: (
        <SelectCheckbox
          checked={selection.allSelected}
          indeterminate={selection.someSelected}
          onChange={selection.toggleAll}
          ariaLabel="Select all contacts on this page"
        />
      ),
      render: (c: Contact) => (
        <SelectCheckbox
          checked={selection.isSelected(c.id)}
          onChange={() => selection.toggle(c.id)}
          ariaLabel={`Select ${c.name}`}
        />
      ),
      className: 'w-10',
    } as Column<Contact>] : []),
    {
      key: 'name',
      header: 'Name',
      render: (c) => (
        <div className="flex items-center gap-3">
          <Avatar name={c.company || c.name} size="sm" />
          <div className="min-w-0">
            <div className="truncate font-semibold text-on-surface">{c.name}</div>
            {c.position && <div className="truncate text-xs text-on-surface-variant font-medium">{c.position}</div>}
          </div>
        </div>
      ),
    },
    { key: 'company', header: 'Company', render: (c) => <span className="font-semibold text-on-surface-variant">{c.company || '—'}</span> },
    {
      key: 'email',
      header: 'Email',
      render: (c) =>
        c.email ? (
          <a
            href={`mailto:${c.email}`}
            onClick={(e) => e.stopPropagation()}
            className="text-info-text font-medium hover:underline"
          >
            {c.email}
          </a>
        ) : (
          '—'
        ),
    },
    {
      key: 'phone',
      header: 'Phone',
      render: (c) =>
        c.phone ? (
          <a
            href={`tel:${c.phone}`}
            onClick={(e) => e.stopPropagation()}
            className="tabular-nums text-on-surface-variant font-medium hover:text-info-text"
          >
            {c.phone}
          </a>
        ) : (
          '—'
        ),
    },
    {
      key: 'creator_name',
      header: 'Owner / Created By',
      render: (c: Contact) => (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-muted text-on-surface-variant">
          {c.creator_name || 'Team Member'}
        </span>
      ),
    },
  ];

  return (
    <div className="mx-auto flex h-full max-w-[1580px] flex-col space-y-5 pb-16">
      <PageHeader
        icon={Users}
        eyebrow="Sales"
        title="Contacts"
        description={currentUser?.role === 'field_executive'
          ? 'Your contacts — the ones you added and the ones assigned to you.'
          : 'The shared client directory — every company and person the team deals with, with the BDM who owns each.'}
        actions={<>
          {/* Open to everyone who can see this page: the whole point is finding a
              caller who is NOT in your own list. */}
          <Button variant="outline" onClick={() => setCallerLookupOpen(true)}>
            <PhoneIncoming className="h-4 w-4" /> Caller Lookup
          </Button>
          {canCreate && (
            <Button onClick={handleCreate}>
              <Plus className="h-4 w-4" /> New Contact
            </Button>
          )}
        </>}
      />

      <StatGrid cols={3}>
        <StatCard label="Total Contacts" value={total.toLocaleString('en-IN')} hint="In the CRM directory" loading={isLoading} icon={Users} tone="primary" />
        <StatCard
          label="Email Reachable" loading={isLoading}
          value={withEmailCount.toLocaleString('en-IN')}
          hint={total > 0 ? `${Math.round((withEmailCount / total) * 100)}% of contacts have an email` : 'Have an email address'}
          icon={Mail}
          tone="info"
          progress={total > 0 ? (withEmailCount / total) * 100 : undefined}
        />
        {/* The progress bar here was a hard-coded 80% — there is no real share to show. */}
        <StatCard label="Unique Companies" loading={isLoading} value={companyCount.toLocaleString('en-IN')} hint="Distinct client accounts" icon={Building2} tone="success" />
      </StatGrid>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-card p-4 rounded-card border border-border shadow-xs">
        <div className="flex flex-wrap items-center gap-3 flex-1">
          <div className="relative min-w-[240px] flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-on-surface-subtle" />
            <input
              className="w-full pl-9 pr-3 py-2 rounded-xl text-xs font-medium border border-border bg-muted/50 focus:outline-none focus:ring-2 focus-visible:ring-ring focus:bg-card transition-all"
              placeholder="Search name, company, email, phone…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
          <div className="w-56">
            <Select
              name="contacts-company-filter"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              options={companyOptions}
            />
          </div>
          <div className="w-48">
            <Select
              name="contacts-sort"
              value={sort}
              onChange={(e) => setSort(e.target.value as ContactsSort)}
              options={SORT_OPTIONS}
            />
          </div>
          {/* Whole-directory roles only — a plain rep's own list is already
              scoped to themself, so this would be a dropdown of one. */}
          {canSeeWholeDirectory && (
            <div className="w-56">
              <Select
                name="contacts-created-by-filter"
                value={createdBy}
                onChange={(e) => setCreatedBy(e.target.value)}
                options={[
                  { value: '', label: 'All users' },
                  ...userOptions.map((u) => ({ value: u.id, label: u.display_name })),
                ]}
              />
            </div>
          )}
        </div>
        <div className="text-xs font-semibold text-on-surface-variant">
          Showing <span className="text-on-surface font-bold">{rangeStart}-{rangeEnd}</span> of{' '}
          <span className="text-on-surface font-bold">{total}</span> contacts
        </div>
      </div>

      {/* Bulk action bar — only when at least one row is selected */}
      {canBulkEmail && selection.count > 0 && (
        <div className="flex items-center justify-between gap-3 rounded-2xl border border-info/30 bg-info/10 px-4 py-3">
          <span className="text-xs font-bold text-info-text">
            {selection.count} contact{selection.count === 1 ? '' : 's'} selected
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setBulkEmailOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-primary text-white hover:bg-primary-dim transition-colors"
            >
              <Send className="h-3.5 w-3.5" /> Send Bulk Email
            </button>
            <button
              onClick={selection.clear}
              className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-semibold text-info-text hover:bg-info/10 transition-colors"
            >
              <X className="h-3.5 w-3.5" /> Clear
            </button>
          </div>
        </div>
      )}

      {/* Body */}
      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      ) : isError ? (
        <EmptyState title="Failed to load contacts" description="Check your connection and try again." action={<Button onClick={() => refetch()}>Retry</Button>} />
      ) : visible.length === 0 ? (
        <EmptyState
          title={search || company || createdBy ? 'No matching contacts' : 'No contacts yet'}
          description={
            search || company || createdBy ? 'Try adjusting your search or filter.' : 'Create your first contact to build the customer database.'
          }
          action={!search && !company && !createdBy && canCreate ? <Button onClick={handleCreate}>New Contact</Button> : undefined}
        />
      ) : (
        <div className="flex-1 overflow-y-auto">
          <Table columns={columns} data={visible} rowKey={(c) => c.id} onRowClick={handleRowClick} empty="No contacts found." />
          {totalPages > 1 && (
            <div className="mt-4 flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-xs font-semibold text-on-surface-variant">
                Page <span className="text-on-surface">{page + 1}</span> of <span className="text-on-surface">{totalPages}</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0 || isFetching}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-xs font-bold text-on-surface-variant transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <ChevronLeft className="h-4 w-4" /> Previous
                </button>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1 || isFetching}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-xs font-bold text-on-surface-variant transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {canBulkEmail && (
        <ContactsBulkEmailModal
          open={bulkEmailOpen}
          onClose={() => setBulkEmailOpen(false)}
          selectedIds={selection.selectedIds}
          onSent={selection.clear}
        />
      )}

      <CallerLookupModal open={callerLookupOpen} onClose={() => setCallerLookupOpen(false)} />
    </div>
  );
}
